import { conditionsForScope, type SubjectScopeMap } from "@msuite/citadel";
import type { PermissionScopeTagValue } from "@msuite/ui/tag";
import type { TranslationKey } from "@msuite/utils/locales";

/**
 * The explanation of one scope option, as both permission grids render it under the tag inside
 * the open list. Subject-aware where the scope is: `own` binds `userId` on the subjects whose
 * rows are ABOUT the person (their absences, their hours) and `createdBy` on the ones they
 * authored (their invoices, their tickets), and the sentence has to say which — so it is read
 * off the subject's own conditions template rather than maintained per row.
 *
 * @param subject - The manifest subject the cell is on.
 * @param scope - The tag value the option renders, display states included.
 * @returns The translation key of the sentence explaining what the option grants.
 */
export function scopeExplanationKey(
	subject: keyof typeof SubjectScopeMap,
	scope: PermissionScopeTagValue,
): TranslationKey {
	switch (scope) {
		case "none":
			return "roles.scope_explanation_none";
		case "organization":
			return "roles.scope_explanation_organization";
		case "own": {
			const template = conditionsForScope(subject, "own");
			return template && "userId" in template
				? "roles.scope_explanation_own_records"
				: "roles.scope_explanation_own_created";
		}
		case "ownProjects":
			return "roles.scope_explanation_own_projects";
		case "custom":
			return "roles.scope_explanation_custom";
		case "combined":
			return "roles.scope_explanation_combined";
	}
}
